import React from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardMedia, CircularProgress,
  Grid, IconButton, InputAdornment, MenuItem, TextField, Typography,
} from '@mui/material';
import { CloudUpload, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { PropertyStatus, PropertyType, UnitStatus } from '../../../types';
import LocationPicker from '../../Map/LocationPicker';
import { FEATURE_OPTIONS, LEBANON_GOVERNORATES, paymentMethods, rentPaymentPeriods, transactionTypes } from './propertyFormConstants';
import { PropertyFormData } from './propertyFormUtils';

export interface PropertyFormAgentOption {
  id: string;
  user?: { firstName: string; lastName: string } | null;
}

export interface PropertyFormProjectOption {
  id: string;
  name: string;
  totalFloors?: number;
  ownerName?: string;
  ownerPhone?: string;
  constructionStatus?: string;
  latitude?: number;
  longitude?: number;
  governorate?: string;
  district?: string;
  town?: string;
  address?: string;
  city?: string;
  googleMapsLink?: string;
}

interface Props {
  formData: PropertyFormData;
  errors: Record<string, string>;
  districtOptions: string[];
  agents: PropertyFormAgentOption[];
  projects: PropertyFormProjectOption[];
  uploadedImages: string[];
  uploadError: string;
  uploadingImages: boolean;
  canEditAgentAssignment: boolean;
  canEditStatusSelection: boolean;
  onChange: (field: keyof PropertyFormData, value: any) => void;
  onProjectSelect?: (ownerName: string, ownerPhone: string) => void;
  onLocationChange: (lat: number, lng: number, googleMapsLink?: string) => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>;
  onRemoveImage: (index: number) => void;
  onClearUploadError: () => void;
}

const PropertyFormFields: React.FC<Props> = ({
  formData, errors, districtOptions, agents, projects,
  uploadedImages, uploadError, uploadingImages,
  canEditAgentAssignment, canEditStatusSelection,
  onChange, onProjectSelect, onLocationChange, onImageUpload, onRemoveImage, onClearUploadError,
}) => {
  const selectedProject = projects.find((p) => p.id === formData.projectId);
  const isUnderConstructionProject = selectedProject?.constructionStatus === 'UNDER_CONSTRUCTION';
  const needsLandAndInteriorSize = [PropertyType.VILLA, PropertyType.TOWNHOUSE, PropertyType.BUILDING, PropertyType.UNIT].includes(formData.propertyType);

  return (
    <Grid container spacing={2.5}>

      {/* ── Title ── */}
      <Grid item xs={12}>
        <TextField fullWidth required label="Property Title"
          value={formData.title} onChange={(e) => onChange('title', e.target.value)}
          error={!!errors.title} helperText={errors.title} />
      </Grid>

      {/* ── Type & Status ── */}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select required label="Property Type"
          value={formData.propertyType} onChange={(e) => onChange('propertyType', e.target.value)}>
          {Object.values(PropertyType).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select required label="Status"
          value={formData.status} onChange={(e) => onChange('status', e.target.value)}
          disabled={!canEditStatusSelection}
          helperText={!canEditStatusSelection ? 'Status is managed by admin' : undefined}>
          {Object.values(PropertyStatus).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Grid>

      {/* ── Transaction & Price ── */}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select required label="Transaction Type"
          value={formData.transactionType} onChange={(e) => onChange('transactionType', e.target.value)}
          helperText="For rent or sale?">
          <MenuItem value=""><em>Not specified</em></MenuItem>
          {transactionTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Price" type="number"
          value={formData.price} onChange={(e) => onChange('price', e.target.value)}
          error={!!errors.price} helperText={errors.price}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
      </Grid>

      {/* ── Location ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Location</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete options={LEBANON_GOVERNORATES} value={formData.governorate || null}
          onChange={(_, v) => onChange('governorate', v || '')} freeSolo={false}
          renderInput={(params) => (
            <TextField {...params} required label="Governorate" placeholder="Select Governorate"
              error={!!errors.governorate} helperText={errors.governorate} />
          )} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete options={districtOptions} value={formData.district || null}
          onChange={(_, v) => onChange('district', v || '')} freeSolo={false}
          disabled={!formData.governorate}
          renderInput={(params) => (
            <TextField {...params} label="District"
              placeholder={formData.governorate ? 'Select District' : 'Select Governorate first'} />
          )} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Town/City" value={formData.town}
          onChange={(e) => onChange('town', e.target.value)}
          placeholder="e.g., Hamra, Ashrafieh, Jounieh" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="City" value={formData.city}
          onChange={(e) => onChange('city', e.target.value)}
          error={!!errors.city} helperText={errors.city} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth required label="Address" value={formData.address}
          onChange={(e) => onChange('address', e.target.value)}
          error={!!errors.address} helperText={errors.address}
          placeholder="Street name, building number, floor" />
      </Grid>

      {/* ── Property Details (type-specific) ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Property Details</Typography>
      </Grid>

      {formData.propertyType === PropertyType.WAREHOUSE ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Size (m²)" type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Warehouse Type" value={formData.warehouseType}
              onChange={(e) => onChange('warehouseType', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="Industrial">Industrial</MenuItem>
              <MenuItem value="Residential">Residential</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Float" type="number" value={formData.warehouseFloat}
              onChange={(e) => onChange('warehouseFloat', e.target.value)}
              placeholder="Enter float value" inputProps={{ step: 0.01 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Access Type" value={formData.warehouseAccessType}
              onChange={(e) => onChange('warehouseAccessType', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="Stairs">Stairs</MenuItem>
              <MenuItem value="Slope">Slope</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Vehicle Type" value={formData.vehicleType}
              onChange={(e) => onChange('vehicleType', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="Pickup">Pickup</MenuItem>
              <MenuItem value="Sixwheel">Sixwheel</MenuItem>
            </TextField>
          </Grid>
        </>
      ) : formData.propertyType === PropertyType.OFFICE ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Size (m²)" type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          {!isUnderConstructionProject && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Property Age (Years)" type="number" value={formData.propertyAge}
                onChange={(e) => onChange('propertyAge', e.target.value)} placeholder="Age of building"
                error={!!errors.propertyAge} helperText={errors.propertyAge} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor" type="number" value={formData.floor}
              onChange={(e) => onChange('floor', e.target.value)} placeholder="Which floor is the office on?"
              error={!!errors.floor} helperText={errors.floor} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Total Floors" type="number" value={formData.totalFloors}
              onChange={(e) => onChange('totalFloors', e.target.value)}
              placeholder="Total number of floors in building"
              error={!!errors.totalFloors} helperText={errors.totalFloors} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Elevator" value={formData.elevator}
              onChange={(e) => onChange('elevator', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Furnished" value={formData.furnished}
              onChange={(e) => onChange('furnished', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="semi">Semi-Furnished</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Kitchen" value={formData.kitchen}
              onChange={(e) => onChange('kitchen', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Rooms" type="number" value={formData.rooms}
              onChange={(e) => onChange('rooms', e.target.value)} placeholder="Number of rooms" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bathrooms" type="number" value={formData.bathrooms}
              onChange={(e) => onChange('bathrooms', e.target.value)}
              error={!!errors.bathrooms} helperText={errors.bathrooms} />
          </Grid>
        </>
      ) : formData.propertyType === PropertyType.SHOP ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Size (m²)" type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          {!isUnderConstructionProject && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Property Age (Years)" type="number" value={formData.propertyAge}
                onChange={(e) => onChange('propertyAge', e.target.value)} placeholder="Age of building"
                error={!!errors.propertyAge} helperText={errors.propertyAge} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor" type="number" value={formData.floor}
              onChange={(e) => onChange('floor', e.target.value)} placeholder="Which floor is the shop on?"
              error={!!errors.floor} helperText={errors.floor} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Total Floors" type="number" value={formData.totalFloors}
              onChange={(e) => onChange('totalFloors', e.target.value)}
              placeholder="Total number of floors in building"
              error={!!errors.totalFloors} helperText={errors.totalFloors} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Elevator" value={formData.elevator}
              onChange={(e) => onChange('elevator', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Rooms" type="number" value={formData.rooms}
              onChange={(e) => onChange('rooms', e.target.value)} placeholder="Number of rooms" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bathrooms" type="number" value={formData.bathrooms}
              onChange={(e) => onChange('bathrooms', e.target.value)}
              error={!!errors.bathrooms} helperText={errors.bathrooms} />
          </Grid>
        </>
      ) : formData.propertyType === PropertyType.SHOWROOM ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Size (m²)" type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          {!isUnderConstructionProject && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Property Age (Years)" type="number" value={formData.propertyAge}
                onChange={(e) => onChange('propertyAge', e.target.value)} placeholder="Age of building"
                error={!!errors.propertyAge} helperText={errors.propertyAge} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor" type="number" value={formData.floor}
              onChange={(e) => onChange('floor', e.target.value)} placeholder="Which floor is the showroom on?"
              error={!!errors.floor} helperText={errors.floor} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Total Floors" type="number" value={formData.totalFloors}
              onChange={(e) => onChange('totalFloors', e.target.value)}
              placeholder="Total number of floors in building"
              error={!!errors.totalFloors} helperText={errors.totalFloors} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Elevator" value={formData.elevator}
              onChange={(e) => onChange('elevator', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bathrooms" type="number" value={formData.bathrooms}
              onChange={(e) => onChange('bathrooms', e.target.value)}
              error={!!errors.bathrooms} helperText={errors.bathrooms} />
          </Grid>
        </>
      ) : formData.propertyType === PropertyType.LAND ? (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Land Size (m²)" type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Land Type" value={formData.landType}
              onChange={(e) => onChange('landType', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="Agricultural">Agricultural</MenuItem>
              <MenuItem value="Industrial">Industrial</MenuItem>
              <MenuItem value="Residential">Residential</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Zone" value={formData.zone}
              onChange={(e) => onChange('zone', e.target.value)} placeholder="e.g., R1, C1, I1" />
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={needsLandAndInteriorSize ? 'Interior Size (m²)' : 'Size (m²)'} type="number" value={formData.size}
              onChange={(e) => onChange('size', e.target.value)} error={!!errors.size} helperText={errors.size}
              InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
          </Grid>
          {needsLandAndInteriorSize && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Land Size (m²)" type="number" value={formData.landSize}
                onChange={(e) => onChange('landSize', e.target.value)} error={!!errors.landSize} helperText={errors.landSize}
                InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} />
            </Grid>
          )}
          {!isUnderConstructionProject && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Property Age (Years)" type="number" value={formData.propertyAge}
                onChange={(e) => onChange('propertyAge', e.target.value)} placeholder="Age of building"
                error={!!errors.propertyAge} helperText={errors.propertyAge} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bedrooms" type="number" value={formData.bedrooms}
              onChange={(e) => onChange('bedrooms', e.target.value)}
              error={!!errors.bedrooms} helperText={errors.bedrooms} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bathrooms" type="number" value={formData.bathrooms}
              onChange={(e) => onChange('bathrooms', e.target.value)}
              error={!!errors.bathrooms} helperText={errors.bathrooms} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor" type="number" value={formData.floor}
              onChange={(e) => onChange('floor', e.target.value)} placeholder="Which floor is the property on?"
              error={!!errors.floor} helperText={errors.floor} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Total Floors" type="number" value={formData.totalFloors}
              onChange={(e) => onChange('totalFloors', e.target.value)}
              placeholder="Total number of floors in building"
              error={!!errors.totalFloors} helperText={errors.totalFloors} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Elevator" value={formData.elevator}
              onChange={(e) => onChange('elevator', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Furnished" value={formData.furnished}
              onChange={(e) => onChange('furnished', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="semi">Semi-Furnished</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Maid Room" value={formData.maidRoom}
              onChange={(e) => onChange('maidRoom', e.target.value)}>
              <MenuItem value=""><em>Not specified</em></MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Water Tanks (L)" type="number" value={formData.waterTanks}
              onChange={(e) => onChange('waterTanks', e.target.value)}
              placeholder="e.g. 500"
              InputProps={{ endAdornment: <InputAdornment position="end">L</InputAdornment> }}
              inputProps={{ min: 0, step: 1 }} />
          </Grid>
        </>
      )}

      {/* ── Parking (all types except LAND) ── */}
      {formData.propertyType !== PropertyType.LAND && (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Parking Spots" type="number" value={formData.parking}
            onChange={(e) => onChange('parking', e.target.value)}
            placeholder="Number of parking spots"
            inputProps={{ min: 0, step: 1 }} />
        </Grid>
      )}

      {/* ── Owner Information ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Owner Information</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Owner Name" value={formData.ownerName}
          onChange={(e) => onChange('ownerName', e.target.value)}
          placeholder="Full name of property owner"
          error={!!errors.ownerName}
          helperText={errors.ownerName || (formData.projectId && formData.ownerName ? 'Auto-filled from project' : undefined)} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Owner Phone" type="tel" value={formData.ownerPhone}
          onChange={(e) => onChange('ownerPhone', e.target.value)}
          placeholder="+961 XX XXX XXX"
          error={!!errors.ownerPhone}
          helperText={errors.ownerPhone || (formData.projectId && formData.ownerPhone ? 'Auto-filled from project' : undefined)} />
      </Grid>

      {/* ── Map Coordinates ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Map Coordinates</Typography>
      </Grid>
      <Grid item xs={12}>
        <LocationPicker lat={formData.latitude} lng={formData.longitude} onLocationChange={onLocationChange} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Latitude" type="number" value={formData.latitude}
          onChange={(e) => onChange('latitude', parseFloat(e.target.value))}
          error={!!errors.latitude} helperText={errors.latitude} inputProps={{ step: 0.000001 }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth required label="Longitude" type="number" value={formData.longitude}
          onChange={(e) => onChange('longitude', parseFloat(e.target.value))}
          error={!!errors.longitude} helperText={errors.longitude} inputProps={{ step: 0.000001 }} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="Google Maps Link" value={formData.googleMapsLink}
          onChange={(e) => onChange('googleMapsLink', e.target.value)}
          placeholder="https://maps.google.com/?q=33.8547,35.8623"
          helperText="Paste Google Maps location link for exact property location" />
      </Grid>

      {/* ── Additional Information ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Additional Information</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Country" value={formData.country}
          onChange={(e) => onChange('country', e.target.value)} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Payment Method (Optional)" value={formData.paymentMethod}
          onChange={(e) => onChange('paymentMethod', e.target.value)} helperText="Preferred payment method">
          <MenuItem value=""><em>Not specified</em></MenuItem>
          {formData.transactionType === 'RENT'
            ? rentPaymentPeriods.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)
            : paymentMethods.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
      </Grid>
      {(formData.paymentMethod === 'DOWN_PAYMENT_FACILITIES' || formData.paymentMethod === 'GOVERNMENT_HOUSING') && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Down Payment Amount" type="number" value={formData.downPayment}
              onChange={(e) => onChange('downPayment', e.target.value)}
              placeholder="e.g., 50000" helperText="Initial down payment amount" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={formData.paymentMethod === 'GOVERNMENT_HOUSING' ? 'Esken Approved' : 'Monthly Payment'} type="number" value={formData.monthlyPayment}
              onChange={(e) => onChange('monthlyPayment', e.target.value)}
              placeholder="e.g., 1500" helperText="Monthly installment amount" />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Assign Agent (Optional)" value={formData.agentId}
          onChange={(e) => onChange('agentId', e.target.value)}
          disabled={!canEditAgentAssignment}
          helperText={!canEditAgentAssignment ? 'Agent assignment is managed by admin' : undefined}>
          <MenuItem value=""><em>Unassigned</em></MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.id} value={agent.id}>
              {agent.user ? `${agent.user.firstName} ${agent.user.lastName}` : 'Unknown Agent'}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* ── Project / Building Link ── */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 1, fontWeight: 600 }}>
          🏢 Project / Building Link (Optional)
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth select label="Link to Project" value={formData.projectId}
          onChange={(e) => {
            const project = projects.find((p) => p.id === e.target.value);
            onChange('projectId', e.target.value);
            if (project) {
              if (onProjectSelect) onProjectSelect(project.ownerName || '', project.ownerPhone || '');
              if (project.latitude !== undefined) onChange('latitude', project.latitude);
              if (project.longitude !== undefined) onChange('longitude', project.longitude);
              if (project.googleMapsLink) onChange('googleMapsLink', project.googleMapsLink);
              if (project.governorate) onChange('governorate', project.governorate);
              if (project.district) onChange('district', project.district);
              if (project.town) onChange('town', project.town);
              if (project.address) onChange('address', project.address);
              if (project.city) onChange('city', project.city);
            }
          }}
          helperText="Optionally link this property to a building/project">
          <MenuItem value=""><em>No project</em></MenuItem>
          {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
      </Grid>
      {formData.projectId && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select label="Unit Status" value={formData.unitStatus}
              onChange={(e) => onChange('unitStatus', e.target.value)}
              helperText="Current availability status of this unit">
              <MenuItem value=""><em>Not set</em></MenuItem>
              {Object.values(UnitStatus).map((s) => (
                <MenuItem key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Floor Number" type="number" value={formData.floorNumber}
              onChange={(e) => onChange('floorNumber', e.target.value)}
              error={!!errors.floorNumber} helperText={errors.floorNumber || `Floor within the linked project (-1, 0, 1 ... ${selectedProject?.totalFloors ?? 'N'})`}
              inputProps={{ min: -1 }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Unit Number" value={formData.unitNumber}
              onChange={(e) => onChange('unitNumber', e.target.value)}
              placeholder="e.g. 3A, 12B, G1" helperText="Apartment / unit identifier" />
          </Grid>
        </>
      )}

      {/* ── Description ── */}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={3} label="Description" value={formData.description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="Describe the property, its features, nearby amenities, etc." />
      </Grid>

      {/* ── Notes ── */}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={formData.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="Internal or additional notes about this property" />
      </Grid>

      {/* ── Features ── */}
      {formData.propertyType !== PropertyType.WAREHOUSE && (
        <Grid item xs={12}>
          <Autocomplete multiple freeSolo options={FEATURE_OPTIONS} value={formData.features}
            onChange={(_, value) => onChange('features', value)} filterSelectedOptions
            renderInput={(params) => (
              <TextField {...params} fullWidth label="Features" placeholder="Select or add features"
                helperText="Choose from common features or type to add custom ones" />
            )} />
        </Grid>
      )}

      {/* ── Image Upload ── */}
      <Grid item xs={12}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon fontSize="small" /> Property Images
          </Typography>
          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}
            startIcon={uploadingImages ? <CircularProgress size={20} /> : <CloudUpload />}
            disabled={uploadingImages}>
            {uploadingImages ? 'Uploading...' : 'Upload Images'}
            <input type="file" hidden accept="image/*" multiple onChange={onImageUpload} />
          </Button>
        </Box>
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onClearUploadError}>{uploadError}</Alert>
        )}
        {uploadedImages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {uploadedImages.map((url, index) => (
              <Card key={`${url}-${index}`} sx={{ position: 'relative', width: 120, height: 120 }}>
                <CardMedia component="img" height="120" image={url} alt={`Property ${index + 1}`}
                  sx={{ objectFit: 'cover' }} />
                <IconButton size="small" onClick={() => onRemoveImage(index)}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.9)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Card>
            ))}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Supported: JPEG, PNG, GIF, WebP (max 32MB per image)
        </Typography>
      </Grid>

    </Grid>
  );
};

export default PropertyFormFields;
